import { yupResolver } from '@hookform/resolvers/yup'
import { AxiosError } from 'axios'
import { FC, useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { useSelectedProjectPremises } from '@/api/models/main/hooks/useSelectedProjectPremises'
import { useCreateServiceRequest } from '@/api/models/requests/hooks/useCreateRequest'
import {
  CreateServiceRequestDTO,
  DesiredDateType,
  RequestByIdApiType
} from '@/api/models/requests/lib/requests.types'
import {
  extractFilesForSubmit,
  loadRequestFormFiles
} from '@/api/models/requests/lib/requests.utils'
import { ServerValidationError } from '@/api/shared/types'
import { useSelectedProjectStore } from '@/components/entities/common/model/selected-project.store'
import { PageType } from '@/components/entities/requests/model/types'
import { RequestCommentFormBlock } from '@/components/entities/requests/ui/FormBlock/RequestCommentFormBlock'
import { RequestDesiredExecutionTimeFormBlock } from '@/components/entities/requests/ui/FormBlock/RequestDesiredExecutionTimeFormBlock'
import RequestFileUploaderFormBlock from '@/components/entities/requests/ui/FormBlock/RequestFileUploaderFormBlock'
import { RequestRoomPickerFormBlock } from '@/components/entities/requests/ui/FormBlock/RequestRoomPickerFormBlock'
import {
  CommonRequestFormData,
  generateCommonRequestSchema
} from '@/components/entities/requests/ui/model/schemas'
import {
  getDefaultRequestFiles,
  getDefaultRoom
} from '@/components/entities/requests/ui/model/utils'
import { useUploadProgress } from '@/components/features/upload-files/hooks/useUploadProgress'
import { DrawerFormLayout } from '@/components/shared/drawer-form-layout'
import { useRequestNavigation } from '@/components/widgets/request/hooks/useRequestNavigation'
import { isServerValidationError } from '@/utils/parseServerErrorField'

type Props = {
  defaultPremiseId?: string
  pageType: PageType
  onFormSuccess: (_: boolean) => void
  onFormError: (error: AxiosError<ServerValidationError>) => void
  onFormDirty?: (_: boolean) => void
  description?: string
  request?: RequestByIdApiType
  disablePremises?: boolean
  showTimePicker?: boolean
}

const RequestForm: FC<Props> = ({
  defaultPremiseId,
  pageType,
  onFormSuccess,
  onFormError,
  onFormDirty,
  description,
  request,
  disablePremises,
  showTimePicker = true
}) => {
  const { selectedProjectId: projectId } = useSelectedProjectStore()
  const { rooms } = useSelectedProjectPremises()

  const { setUploadProgress, isUploading } = useUploadProgress()
  const {
    mutateAsync: createRequest,
    error: requestError,
    isPending
  } = useCreateServiceRequest()
  const { navigateToRequestDetailsSuccess } = useRequestNavigation()

  const form = useForm<CommonRequestFormData>({
    defaultValues: {
      room: getDefaultRoom(rooms, defaultPremiseId || request?.premiseId),
      description: request?.description ?? description ?? '',
      files: getDefaultRequestFiles(request)
    },
    mode: 'onSubmit',
    resolver: yupResolver(generateCommonRequestSchema())
  })

  const { formState, watch, handleSubmit, setValue } = form

  const dirtyFieldsCount = Object.keys(formState.dirtyFields).length
  const desiredDateType = watch('desiredDateType')

  useEffect(() => {
    if (!request?.files?.length) {
      return
    }

    const previewFiles = getDefaultRequestFiles(request)

    setValue('files', previewFiles)

    let cancelled = false

    loadRequestFormFiles(previewFiles).then(files => {
      if (!cancelled && files.some(file => file.file.size > 0)) {
        setValue('files', files)
      }
    })

    return () => {
      cancelled = true
    }
  }, [request?.id, request?.files, setValue])

  useEffect(() => {
    // Проверка desiredDateType необходима, тк значение всегда передается в форму,
    // независимо от действий пользователя
    const isDirty =
      dirtyFieldsCount > 0 && desiredDateType !== DesiredDateType.NONE

    onFormDirty?.(isDirty)
  }, [dirtyFieldsCount, desiredDateType, onFormDirty])

  const onSubmit = async (data: CommonRequestFormData) => {
    try {
      const files = await extractFilesForSubmit(data.files)

      const body: CreateServiceRequestDTO = {
        projectId,
        description: data.description,
        files,
        premiseId: data.room?.id,
        personalAccountId: data.personalAccount?.id,
        desiredDateType: data.desiredDateType || DesiredDateType.NONE,
        desiredDateInterval: data.desiredDateInterval,
        'desiredDateInterval.start': data.desiredDateInterval?.start,
        'desiredDateInterval.end': data.desiredDateInterval?.end
      }

      const response = await createRequest(body)

      if (response.data?.id && !description?.length) {
        navigateToRequestDetailsSuccess(pageType, response.data.id)
      }

      onFormSuccess(true)
    } catch (error) {
      const axiosError = error as AxiosError<ServerValidationError>
      const isValidationError = isServerValidationError(axiosError)
      if (isValidationError) {
        const shouldShowOnForm = axiosError.response?.data.errors?.some(
          ({ field }) =>
            field === 'files' || field.includes('desiredDateInterval')
        )

        if (shouldShowOnForm) {
          return
        }
      }
      onFormError(error as AxiosError<ServerValidationError>)
    } finally {
      onFormDirty?.(false)
    }
  }

  return (
    <DrawerFormLayout
      onSubmit={handleSubmit(onSubmit)}
      isPending={isPending}
      submitButtonClassName="mt-8"
    >
      <div className="rounded-16 bg-grey-10 p-32 md:p-16">
        <FormProvider {...form}>
          <RequestRoomPickerFormBlock disabled={disablePremises} />
        </FormProvider>
      </div>

      <div className="form-block-cover mb-24 mt-16">
        <FormProvider {...form}>
          <div className="form-block-cover mb-24 xs:p-16">
            <RequestCommentFormBlock
              title="Что необходимо сделать"
              placeholder="Описание"
              required
              inputTestId="request-comment-input-id"
              counterTestId="request-comment-length-counter-id"
            />
            {showTimePicker && (
              <RequestDesiredExecutionTimeFormBlock
                requestError={requestError}
              />
            )}
            <RequestFileUploaderFormBlock
              disabled={false}
              isUploading={isUploading}
              setUploadProgress={setUploadProgress}
              requestError={requestError}
              className="sm:px-0"
            />
          </div>
        </FormProvider>
      </div>
    </DrawerFormLayout>
  )
}

export { RequestForm }
